import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { initJanusForFourVehicle } from "../../utils/fourvehicleVideo";
import authApiService from "../../services/auth-api.service";
import operatorApiService from "../../services/operator-api.service";
import startLayoutCheckFourvehicle from "./utils/startLayoutCheckFourvehicle";
import { setPlayoutDelay } from "../../utils/setPlayoutDelay";
import { FourvehicleLayoutData } from "../../types/fourvehicle";

import "./FourVehicle.scss";
import unplayIcon from "../../assets/imgs/fourvehicle/unplay_icon.png";

const FourVehicle = () => {
  const navigate = useNavigate();
  const videoRefs = useRef<Record<string, HTMLVideoElement | null>>({});
  const remoteFeedsRef = useRef<Record<string, any>>({});
  const suidsRef = useRef<Record<string, string>>({});

  const [hasSignalMap, setHasSignalMap] = useState<Record<string, boolean>>({});
  const [layoutData, setLayoutData] = useState<FourvehicleLayoutData | null>(
    null
  );
  const [baseId, setBaseId] = useState<number | null>(null);
  const [monitorConsoleId, setMonitorConsoleId] = useState<number | null>(null);
  const [janusServer, setJanusServer] = useState<string | null>(null);
  const [janusSdkServer, setJanusSdkServer] = useState<string | null>(null);
  const [sdkServerUserInfo, setSdkServerUserInfo] = useState<any | null>(null);

  const vehicleList =
    layoutData?.layoutInfo
      .sort((a, b) => a.layout_id - b.layout_id)
      .map((layout) => ({
        id: layout.layout_id,
        label: layout.vehicle_label,
        vcc: layout.vehicle_communication_code,
        isDummy: false,
      })) ?? [];

  const filledVehicleList: {
    id: number;
    label: string;
    vcc: string;
    isDummy: boolean;
  }[] = [];

  for (let i = 1; i <= 4; i++) {
    const match = vehicleList.find((v) => v.id === i);
    if (match) {
      filledVehicleList.push(match);
    } else {
      filledVehicleList.push({
        id: i,
        label: "未選択",
        vcc: "dummy",
        isDummy: true,
      });
    }
  }

  useEffect(() => {
    const fetchMe = async () => {
      try {
        const user = await authApiService.me();

        if (user.role_cd !== "02") {
          navigate("/login");
          return;
        }

        setBaseId(user.base_id);
        setMonitorConsoleId(user.monitor_console_id);
      } catch (error) {
        navigate("/login");
      }
    };

    fetchMe();
  }, [navigate]);

  useEffect(() => {
    if (monitorConsoleId === null) return;

    const fetchInitData = async () => {
      try {
        const janusConfig = await operatorApiService.doGetJanusConfig({
          baseId,
        });

        setJanusServer(janusConfig.janusServer);
        setJanusSdkServer(janusConfig.janusSdkServer);
        setSdkServerUserInfo({ credential: janusConfig.credential });

        const response = await operatorApiService.doGetFourvehicleLayoutCheck({
          monitorConsoleId,
        });
        setLayoutData(response);
      } catch (error) {
        console.error("初期データ取得エラー:", error);
      }
    };
    fetchInitData();
  }, [monitorConsoleId]);

  useEffect(() => {
    if (!layoutData || !janusServer || !janusSdkServer || !sdkServerUserInfo)
      return;

    layoutData.layoutInfo.forEach((layout) => {
      const videoEl = videoRefs.current[layout.vehicle_communication_code];
      if (!videoEl) return;
      const suid = layout.zao_suid;
      if (!suid) {
        console.warn("suidがありません");
        return;
      }

      initJanusForFourVehicle({
        janusServer,
        janusSdkServer,
        sdkServerUserInfo,
        suid,
        videoEl,
        onError: (err) => {
          console.error("Janus接続エラー", err);
          setHasSignalMap((prev) => ({
            ...prev,
            [layout.vehicle_communication_code]: false,
          }));
        },
        onStreamStart: () => {
          setHasSignalMap((prev) => ({
            ...prev,
            [layout.vehicle_communication_code]: true,
          }));
        },
        onRemoteHandle: (handle) => {
          remoteFeedsRef.current[layout.vehicle_communication_code] = handle;
          suidsRef.current[layout.vehicle_communication_code] = layout.zao_suid;
        },
      });
    });
  }, [layoutData]);

  useEffect(() => {
    if (!layoutData || monitorConsoleId === null) return;

    const interval = setInterval(() => {
      startLayoutCheckFourvehicle(layoutData, monitorConsoleId);
    }, 1000);

    return () => clearInterval(interval);
  }, [layoutData]);

  useEffect(() => {
    if (!janusSdkServer || !sdkServerUserInfo) return;

    const interval = setInterval(() => {
      setPlayoutDelay(
        remoteFeedsRef.current,
        suidsRef.current,
        janusSdkServer,
        sdkServerUserInfo
      );
    }, 1000);

    return () => clearInterval(interval);
  }, [sdkServerUserInfo]);

  return (
    <div className="fourVideoWrapper">
      <div className="fourVideoGrid">
        {filledVehicleList.map((vehicle) => (
          <div key={vehicle.id} className="fourVideoBlock">
            <div className="fourVideoItem">
              <div className="fourVideoDisplay">
                {vehicle.isDummy || !hasSignalMap[vehicle.vcc] ? (
                  <>
                    <div className="fourVideoOverlay">
                      <img
                        src={unplayIcon}
                        alt="No Signal"
                        className="noSignalIcon"
                      />
                    </div>
                  </>
                ) : null}
                {!vehicle.isDummy && (
                  <video
                    ref={(el) => {
                      videoRefs.current[vehicle.vcc] = el;
                    }}
                    autoPlay
                    muted
                    playsInline
                    style={
                      !vehicle.isDummy && hasSignalMap[vehicle.vcc]
                        ? { display: "block" }
                        : { display: "none" }
                    }
                  />
                )}
              </div>
            </div>
            <div className="fourVideoLabel">{vehicle.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FourVehicle;
